/**
 * 格式化工具函数
 */

/**
 * 格式化金额，添加千分位分隔符
 * @param {number|string} amount - 金额
 * @param {object} options - 格式化选项
 * @param {number} options.decimals - 小数位数，默认0
 * @param {string} options.separator - 千分位分隔符，默认','
 * @param {string} options.prefix - 前缀，默认''
 * @param {string} options.suffix - 后缀，默认''
 * @returns {string} 格式化后的金额字符串
 */
export const formatAmount = (amount, options = {}) => {
  const {
    decimals = 0,
    separator = ',',
    prefix = '',
    suffix = ''
  } = options;

  if (amount === null || amount === undefined || amount === '') {
    return '0';
  }

  // 转换为数字
  const num = parseFloat(amount);
  if (isNaN(num)) {
    return '0';
  }

  // 格式化小数位
  const fixed = num.toFixed(decimals);
  
  // 分离整数和小数部分
  const parts = fixed.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // 添加千分位分隔符
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  
  // 组合结果
  let result = formattedInteger;
  if (decimals > 0 && decimalPart) {
    result += '.' + decimalPart;
  }

  return prefix + result + suffix;
};

/**
 * 格式化人民币金额
 * @param {number|string} amount - 金额
 * @param {boolean} showSymbol - 是否显示货币符号，默认true
 * @param {number} decimals - 小数位数，默认0
 * @returns {string} 格式化后的人民币金额
 */
export const formatRMB = (amount, showSymbol = true, decimals = 0) => {
  return formatAmount(amount, {
    decimals,
    prefix: showSymbol ? '¥' : '',
    separator: ','
  });
};

/**
 * 格式化百分比
 * @param {number} value - 数值（0-1之间或0-100之间）
 * @param {number} decimals - 小数位数，默认1
 * @param {boolean} isPercent - 输入值是否已经是百分比形式，默认false
 * @returns {string} 格式化后的百分比
 */
export const formatPercent = (value, decimals = 1, isPercent = false) => {
  if (value === null || value === undefined || value === '') {
    return '0%';
  }

  const num = parseFloat(value);
  if (isNaN(num)) {
    return '0%';
  }

  const percent = isPercent ? num : num * 100;
  return percent.toFixed(decimals) + '%';
};

/**
 * 格式化手机号，中间4位用*替代
 * @param {string} mobile - 手机号
 * @returns {string} 格式化后的手机号
 */
export const formatMobile = (mobile) => {
  if (!mobile || mobile.length !== 11) {
    return mobile;
  }
  return mobile.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
};

/**
 * 格式化银行卡号，只显示后4位
 * @param {string} cardNumber - 银行卡号
 * @returns {string} 格式化后的银行卡号
 */
export const formatBankCard = (cardNumber) => {
  if (!cardNumber || cardNumber.length < 4) {
    return cardNumber;
  }
  const lastFour = cardNumber.slice(-4);
  return '**** **** **** ' + lastFour;
};

/**
 * 格式化文件大小
 * @param {number} bytes - 字节数
 * @param {number} decimals - 小数位数，默认2
 * @returns {string} 格式化后的文件大小
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * 格式化时间戳为相对时间
 * @param {number|Date} timestamp - 时间戳或Date对象
 * @returns {string} 相对时间字符串
 */
export const formatRelativeTime = (timestamp) => {
  const now = new Date();
  const time = new Date(timestamp);
  const diff = now - time;

  const minute = 60 * 1000;
  const hour = minute * 60;
  const day = hour * 24;
  const month = day * 30;
  const year = day * 365;

  if (diff < minute) {
    return '刚刚';
  } else if (diff < hour) {
    return Math.floor(diff / minute) + '分钟前';
  } else if (diff < day) {
    return Math.floor(diff / hour) + '小时前';
  } else if (diff < month) {
    return Math.floor(diff / day) + '天前';
  } else if (diff < year) {
    return Math.floor(diff / month) + '个月前';
  } else {
    return Math.floor(diff / year) + '年前';
  }
};

export default {
  formatAmount,
  formatRMB,
  formatPercent,
  formatMobile,
  formatBankCard,
  formatFileSize,
  formatRelativeTime
};